import { useCallback, useMemo, type FC } from 'react';

import { useGetOwnChats } from '@/features/api/hooks/grapevine-chat/useGetOwnChats';
import { useStartChatOnComment } from '@/features/api/hooks/grapevine-chat-events/useStartChatOnComment';
import { useGetComment } from '@/features/api/hooks/grapevine-comment/useGetComment';
import { useAppState } from '@/features/authorization/provider/useAppState';
import { NewChatOnEntity } from '@/features/chats/components/NewChatOnEntity';
import { findChatBetweenPersons } from '@/features/chats/utils/chat';
import type {
  AppNativeStackScreenProps,
  AppRoute
} from '@/navigation/types/navigation';

type Props = AppNativeStackScreenProps<typeof AppRoute.NewChatOnComment>;

export const ChatOnCommentScreen: FC<Props> = ({ route }) => {
  const commentId = route.params.commentId;

  const { userId: ownUserId } = useAppState();
  const { data: comment, isLoading: isCommentLoading } =
    useGetComment(commentId);
  const { data: chats, isLoading: isChatLoading } = useGetOwnChats();
  const { mutateAsync: startChatOnComment } = useStartChatOnComment(commentId);

  const sendMessage = useCallback(
    async (message: string, temporaryMediaItemIds: string[]) => {
      const response = await startChatOnComment({
        message,
        temporaryMediaItemIds
      });
      return response.chat.id;
    },
    [startChatOnComment]
  );

  const entityPerson = comment?.creator;

  const existingChat = useMemo(
    () =>
      chats &&
      entityPerson &&
      findChatBetweenPersons(chats, ownUserId, entityPerson.id),
    [chats, entityPerson, ownUserId]
  );

  return (
    <NewChatOnEntity
      sendMessage={sendMessage}
      entityPerson={entityPerson}
      existingChat={existingChat}
      loading={isCommentLoading || isChatLoading}
    />
  );
};
